import axios from 'axios';

import { API_BASE_URL, API_TOKEN, STORAGE_KEYS } from '../config';
import { getCommonParams } from './url';
import { safeGet, safeSet } from './storage';
import { resolveSubmissionContext } from './submissionRecovery';
import { assessmentMetadata, responseMetadata } from './assessmentLanguage';
import { persistAssessmentMetadata } from './adultQuestionBank';

export const CHECKPOINT_BATCH_SIZE = 5;
export const CHECKPOINT_MAX_WAIT_MS = 10000;

let checkpointRequest = null;
let checkpointTimer = null;

const getContext = () => resolveSubmissionContext({
  userInfo: safeGet(STORAGE_KEYS.USER_INFO, {}),
  urlParams: getCommonParams(),
  activeSession: safeGet(STORAGE_KEYS.ACTIVE_SESSION, {}),
  sessions: safeGet(STORAGE_KEYS.USER_EVALUATION_SESSIONS, {}),
});

const checkpointUrl = `${API_BASE_URL}/api/assessment/evaluation_checkpoint`;

const normalizeAnswers = (answers) => Object.entries(answers || {}).reduce((result, [no, fraction]) => {
  const parsedNo = parseInt(no, 10);
  const parsedFraction = parseInt(fraction, 10);
  if (parsedNo > 0 && [0, 1, 2].includes(parsedFraction)) {
    result[parsedNo] = parsedFraction;
  }
  return result;
}, {});

const clearCheckpointTimer = () => {
  if (checkpointTimer) {
    window.clearTimeout(checkpointTimer);
    checkpointTimer = null;
  }
};

export const cancelScheduledCheckpoint = clearCheckpointTimer;

const scheduleCheckpoint = () => {
  if (checkpointTimer) return;
  checkpointTimer = window.setTimeout(() => {
    checkpointTimer = null;
    void flushAnswerCheckpoint({ force: true });
  }, CHECKPOINT_MAX_WAIT_MS);
};

export const createPayload = (context, snapshot) => ({
  useruuid: context.useruuid,
  evaluation_uuid: context.evaluation_uuid,
  e_data: {
    defaultLang: context.defaultLang,
    ...assessmentMetadata(context),
    evaluation_type: context.evaluation_type,
    User_NID: context.User_NID,
    companyCode: context.companyCode,
    Job_NID: context.Job_NID,
    codeType: context.codeType,
  },
  e_content: Object.entries(snapshot).map(([no, fraction]) => ({
    no: parseInt(no, 10),
    fraction,
  })),
});

const sendCheckpoint = async (payload, keepalive) => {
  const headers = {
    Authorization: `Bearer ${API_TOKEN}`,
    'Content-Type': 'application/json',
  };
  if (keepalive && typeof window.fetch === 'function') {
    const response = await window.fetch(checkpointUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
      keepalive: true,
    });
    if (!response.ok) throw new Error(`Checkpoint HTTP ${response.status}`);
    return response;
  }
  return axios.post(checkpointUrl, payload, { headers, timeout: 8000 });
};

export const flushAnswerCheckpoint = async ({ force = false, keepalive = false } = {}) => {
  if (checkpointRequest) {
    const inFlightSucceeded = await checkpointRequest;
    return force
      ? flushAnswerCheckpoint({ force: true, keepalive })
      : inFlightSucceeded;
  }

  const context = getContext();
  const snapshot = normalizeAnswers(safeGet(STORAGE_KEYS.UNSYNCED_ANSWERS, {}));
  const answerCount = Object.keys(snapshot).length;

  if (!context.useruuid || !context.evaluation_uuid || !answerCount) {
    return false;
  }
  if (!force && answerCount < CHECKPOINT_BATCH_SIZE) {
    scheduleCheckpoint();
    return false;
  }

  clearCheckpointTimer();
  const payload = createPayload(context, snapshot);
  checkpointRequest = sendCheckpoint(payload, keepalive).then((response) => {
    const metadata = responseMetadata(response.data || {});
    if (Object.keys(metadata).length) persistAssessmentMetadata(context, metadata);
    const latest = safeGet(STORAGE_KEYS.UNSYNCED_ANSWERS, {});
    Object.entries(snapshot).forEach(([no, fraction]) => {
      if (parseInt(latest[no], 10) === fraction) delete latest[no];
    });
    safeSet(STORAGE_KEYS.UNSYNCED_ANSWERS, latest);
    return true;
  }).catch((error) => {
    console.warn('[checkpoint] Answers remain queued for retry:', error?.message || error);
    return false;
  });

  const succeeded = await checkpointRequest;
  checkpointRequest = null;

  const remaining = normalizeAnswers(safeGet(STORAGE_KEYS.UNSYNCED_ANSWERS, {}));
  if (Object.keys(remaining).length) {
    if (force && succeeded) {
      return flushAnswerCheckpoint({ force: true, keepalive });
    }
    scheduleCheckpoint();
  }
  return succeeded;
};

export const queueAnswerCheckpoint = (no, fraction) => {
  const queued = safeGet(STORAGE_KEYS.UNSYNCED_ANSWERS, {});
  queued[no] = fraction;
  safeSet(STORAGE_KEYS.UNSYNCED_ANSWERS, queued);
  if (Object.keys(normalizeAnswers(queued)).length >= CHECKPOINT_BATCH_SIZE) {
    return flushAnswerCheckpoint({ force: true });
  }
  scheduleCheckpoint();
  return Promise.resolve(false);
};

export const queueAnswerSnapshot = (answers) => {
  const queued = safeGet(STORAGE_KEYS.UNSYNCED_ANSWERS, {});
  Object.entries(normalizeAnswers(answers)).forEach(([no, fraction]) => {
    queued[no] = fraction;
  });
  safeSet(STORAGE_KEYS.UNSYNCED_ANSWERS, queued);
  return flushAnswerCheckpoint({ force: true });
};

export const flushCheckpointOnPageHide = () => (
  flushAnswerCheckpoint({ force: true, keepalive: true })
);

export const fetchCheckpointAnswers = async (context = getContext()) => {
  if (!context.useruuid || !context.evaluation_uuid) return [];
  const response = await axios.get(checkpointUrl, {
    params: {
      useruuid: context.useruuid,
      evaluation_uuid: context.evaluation_uuid,
    },
    headers: {
      Authorization: `Bearer ${API_TOKEN}`,
      'Content-Type': 'application/json',
    },
  });
  const metadata = responseMetadata(response.data);
  if (Object.keys(metadata).length) persistAssessmentMetadata(context, metadata);
  return Array.isArray(response.data?.e_content) ? response.data.e_content : [];
};
